"use client"

import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { axiosInstance } from "@/lib/axios"
import { Heart, Trash2 } from "lucide-react"
import { useState } from "react"
import type { ICartData } from "./types"
import { getCookie } from "cookies-next"
import { useRouter } from "next/navigation"
import { useWhitelistMutation } from "@/lib/services/api"

interface CourseActionsProps {
	id: number
	isWhitelist: boolean
	showWhiteList?: boolean
	cartData?: ICartData
	type?: "cart" | "course" | "blog"
	refetch?: () => void
}

export const CourseActions = ({
	id,
	isWhitelist: initialIsWhitelist,
	showWhiteList = true,
	cartData,
	type = "course",
	refetch,
}: CourseActionsProps) => {
	const [isWhitelist, setIsWhitelist] = useState(initialIsWhitelist)
	const router = useRouter()
	const authToken = getCookie("auth-token")

	const { mutate: toggleWhitelist, isPending } = useWhitelistMutation()

	const removeCart = async (cartId: number) => {
		try {
			const res = await axiosInstance.delete(`/api/carts/${cartId}`)
			if (res.status === 200) {
				toast({
					title: "Success",
					description: res.data.message,
				})
				if (refetch) {
					refetch()
				}
			} else {
				toast({
					variant: "destructive",
					title: "Error",
					description: res.data.message,
				})
			}
		} catch {
			toast({
				variant: "destructive",
				title: "Error",
				description: "An error occurred while removing the item from the cart",
			})
		}
	}

	const handleWhitelistToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		if (isPending) return

		if (!authToken) {
			toast({
				variant: "destructive",
				title: "Error",
				description: "Please login to add to favorites",
			})
			router.push("/auth/login/google")
			return
		}

		toggleWhitelist(
			{ id, type, isWhitelist },
			{
				onSuccess: (data) => {
					setIsWhitelist(!isWhitelist)
					toast({
						title: "Success",
						description: data.message,
						variant: isWhitelist ? "destructive" : "default",
					})
					if (refetch) {
						refetch()
					}
				},
				onError: () => {
					toast({
						variant: "destructive",
						title: "Error",
						description: "Failed to update whitelist",
					})
				},
			}
		)
	}

	return (
		<div className="flex justify-center sm:justify-start">
			{showWhiteList && (
				<Button
					variant="ghost"
					size="sm"
					className="hidden h-8 text-destructive text-xs hover:bg-destructive/10 hover:text-destructive sm:text-sm md:flex"
					onClick={handleWhitelistToggle}
					disabled={isPending}
				>
					<Heart className={`mr-1.5 h-4 w-5 sm:h-5 sm:w-5 ${isWhitelist ? "fill-current" : ""}`} />
					<span className="hidden sm:block">
						{isWhitelist ? "Đã thêm vào yêu thích" : "Thêm vào yêu thích"}
					</span>
				</Button>
			)}
			{type === "cart" && cartData && (
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
					onClick={(e) => {
						e.preventDefault()
						removeCart(cartData.id)
					}}
				>
					<Trash2 className="h-4 w-4" />
				</Button>
			)}
		</div>
	)
} 